// BandUtils.js
import { getFirestoreInstance } from './firebaseHelper';
import { 
  doc, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  runTransaction,
  arrayUnion,
  arrayRemove,
  Timestamp,
  collection,
  query,
  where,
  getDocs,
  batch
} from 'firebase/firestore';
import { createBandNotification, NOTIFICATION_TYPES } from './NotificationService';

/**
 * Check if a user is a band admin
 * @param {Object} band - The band data object
 * @param {string} userId - The user ID to check
 * @returns {boolean} - True if the user is an admin
 */
export const isUserBandAdmin = (band, userId) => {
  if (!band || !band.members || !userId) return false;
  
  return band.members.some(member => 
    member.userId === userId && member.role === 'admin'
  );
};

/**
 * Check if a user is a band member (any role)
 * @param {Object} band - The band data object
 * @param {string} userId - The user ID to check
 * @returns {boolean} - True if the user is a member
 */
export const isUserBandMember = (band, userId) => {
  if (!band || !band.members || !userId) return false;
  
  return band.members.some(member => 
    member.userId === userId
  );
};


/**
 * Get admin members from the band data
 * @param {Object} bandData - The band data object
 * @returns {Array} - The list of admin members
 */
export const getAdminMembers = (bandData) => {
  if (!bandData || !bandData.members) return [];
  
  return bandData.members.filter(member => 
    member.role === 'admin'
  );
};

/**
 * Get regular members from the band data
 * @param {Object} bandData - The band data object
 * @returns {Array} - The list of regular members
 */
export const getRegularMembers = (bandData) => {
  if (!bandData || !bandData.members) return [];
  
  return bandData.members.filter(member => 
    member.role === 'member'
  );
};

/**
 * Send a request to join a band, using a transaction to ensure consistency
 * @param {string} bandId - The ID of the band to join
 * @param {Object} userData - The user data for the requestor
 * @returns {Promise<boolean>} - Success status
 */
export const sendBandMembershipRequest = async (bandId, userData) => {
  const db = getFirestoreInstance();
  
  try {
    // First check if band exists
    const bandRef = doc(db, 'bands', bandId);
    const bandDoc = await getDoc(bandRef);
    
    if (!bandDoc.exists()) {
      throw new Error('Band not found');
    }

    const bandData = bandDoc.data();
    const userRef = doc(db, 'users', userData.userId);
    
    // Use a transaction for updating both documents
    return await runTransaction(db, async (transaction) => {
      // Get the latest user data within the transaction
      const userDoc = await transaction.get(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }
      
      const userDataFromDb = userDoc.data();
      
      // Get the latest band data within the transaction
      const currentBandDoc = await transaction.get(bandRef);
      const currentBandData = currentBandDoc.data();
      
      // Validation: Check if user is already a member
      const isAlreadyMember = currentBandData.members?.some(member => 
        member.userId === userData.userId
      );
      
      if (isAlreadyMember) {
        throw new Error('You are already a member of this band');
      }
      
      // Validation: Check if request already exists in band
      const requestAlreadyExists = currentBandData.memberRequests?.some(request => 
        request.userId === userData.userId
      );
      
      if (requestAlreadyExists) {
        throw new Error('You have already sent a request to join this band');
      }
      
      // Validation: Check if request already exists in user's pending requests
      const pendingRequestExists = userDataFromDb.pendingActRequests?.some(request => 
        request.type === 'band' && request.id === bandId
      );
      
      if (pendingRequestExists) {
        throw new Error('You have already sent a request to join this band');
      }
      
      // Create the member request with timestamp
      const timestamp = Timestamp.now();
      const memberRequest = {
        userId: userData.userId,
        userName: userData.userName,
        fullName: userData.fullName,
        profilePictureUrl: userData.profilePictureUrl,
        userType: userData.userType,
        userSubType: userData.userSubType || '',
        timestamp
      };
      
      // Create the pending act request
      const pendingRequest = {
        id: bandId,
        type: 'band',
        name: bandData.bandFullName,
        username: bandData.bandUserName,
        photoUrl: bandData.bandProfilePictureUrl || '',
        timestamp
      };
      
      // Update band document - add to memberRequests
      if (!currentBandData.memberRequests) {
        transaction.update(bandRef, { memberRequests: [memberRequest] });
      } else {
        transaction.update(bandRef, {
          memberRequests: arrayUnion(memberRequest)
        });
      }
      
      // Update user document - add to pendingActRequests
      // Initialize pendingActRequests if it doesn't exist
      if (!userDataFromDb.pendingActRequests) {
        transaction.update(userRef, {
          pendingActRequests: [pendingRequest]
        });
      } else {
        transaction.update(userRef, {
          pendingActRequests: arrayUnion(pendingRequest)
        });
      }

      // Get admin members to send notifications to
const adminMembers = bandData.members.filter(member => member.role === 'admin');
const adminIds = adminMembers.map(admin => admin.userId);

// Create notification for band admins
await createBandNotification({
  bandId,
  type: NOTIFICATION_TYPES.MEMBER_REQUEST,
  title: 'New Join Request',
  message: `${userData.fullName} has requested to join ${bandData.bandFullName}`,
  bandName: bandData.bandFullName,
  recipientIds: adminIds,
  actionBy: {
    userId: userData.userId,
    userName: userData.userName,
    fullName: userData.fullName,
    profilePictureUrl: userData.profilePictureUrl
  }
});
      
      return true;
    });
  } catch (error) {
    console.error('Error sending band membership request:', error);
    throw error;
  }
};

/**
 * Handle a band membership request (accept or reject)
 * @param {string} bandId - The ID of the band
 * @param {Object} requestData - The request data
 * @param {boolean} isAccepted - Whether the request is accepted or rejected
 * @param {Object} respondedBy - The user who responded to the request
 * @returns {Promise<boolean>} - Success status
 */
export const handleMembershipRequest = async (bandId, requestData, isAccepted, respondedBy) => {
  const db = getFirestoreInstance();
  
  try {
    const bandRef = doc(db, 'bands', bandId);
    const userRef = doc(db, 'users', requestData.userId);
    
    // Use a transaction for updating both documents
    return await runTransaction(db, async (transaction) => {
      // Get latest documents within the transaction
      const bandDoc = await transaction.get(bandRef);
      const userDoc = await transaction.get(userRef);
      
      if (!bandDoc.exists()) {
        throw new Error('Band not found');
      }
      
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }
      
      const bandData = bandDoc.data();
      const userData = userDoc.data();
      
      // Validate that the request still exists
      const requestStillExists = bandData.memberRequests?.some(request => 
        request.userId === requestData.userId
      );
      
      if (!requestStillExists) {
        throw new Error('Request no longer exists');
      }
      
      // Create a new array without the request
      const updatedRequests = (bandData.memberRequests || []).filter(
        req => req.userId !== requestData.userId
      );
      
      // Check and filter pendingActRequests
      const updatedPendingRequests = (userData.pendingActRequests || []).filter(
        req => !(req.type === 'band' && req.id === bandId)
      );
      
      // Always remove from band's memberRequests and user's pendingActRequests
      transaction.update(bandRef, {
        memberRequests: updatedRequests
      });
      
      transaction.update(userRef, {
        pendingActRequests: updatedPendingRequests
      });
      
      // Additional actions for accepted requests
      if (isAccepted) {
        // Add user to band members based on userType
        const memberData = {
          userId: requestData.userId,
          userName: requestData.userName,
          fullName: requestData.fullName,
          profilePictureUrl: requestData.profilePictureUrl,
          userType: requestData.userType,
          userSubType: requestData.userSubType || '',
          role: requestData.userType === 'Manager' ? 'manager' : 'member',
          joinedAt: Timestamp.now()
        };
        
        // Create the band record for user's bands array
        const bandRecord = {
          id: bandId,
          name: bandData.bandFullName,
          username: bandData.bandUserName,
          photoUrl: bandData.bandProfilePictureUrl || '',
          genres: bandData.bandGenres || [],
          role: memberData.role
        };
        
        // Create the associated act entry
        const associatedAct = {
          id: bandId,
          type: 'band',
          name: bandData.bandFullName,
          username: bandData.bandUserName,
          photoUrl: bandData.bandProfilePictureUrl || '',
          genres: bandData.bandGenres || [],
          role: memberData.role
        };
        
        // Add to band members
        if (!bandData.members) {
          transaction.update(bandRef, { members: [memberData] });
        } else {
          transaction.update(bandRef, {
            members: arrayUnion(memberData)
          });
        }
        
        // Add to user's bands array
        if (!userData.bands) {
          transaction.update(userRef, { bands: [bandRecord] });
        } else {
          transaction.update(userRef, {
            bands: arrayUnion(bandRecord)
          });
        }
        
        // Add to user's associatedActs for displaying in profile
        if (!userData.associatedActs) {
          transaction.update(userRef, { associatedActs: [associatedAct] });
        } else {
          transaction.update(userRef, {
            associatedActs: arrayUnion(associatedAct)
          });
        }
        
        // Create USER_ADDED notification for all members
        await createBandNotification({
          bandId,
          type: NOTIFICATION_TYPES.USER_ADDED,
          title: 'New Member Added',
          message: `${requestData.fullName} has joined ${bandData.bandFullName}`,
          bandName: bandData.bandFullName,
          recipientIds: [
            ...bandData.members.map(m => m.userId),
            requestData.userId
          ],
          actionBy: {
            userId: respondedBy.userId,
            userName: respondedBy.userName,
            fullName: respondedBy.fullName,
            profilePictureUrl: respondedBy.profilePictureUrl
          }
        });
  
        // Create REQUEST_ACCEPTED notification for the requester
        await createBandNotification({
          bandId,
          type: NOTIFICATION_TYPES.REQUEST_ACCEPTED,
          title: 'Join Request Accepted',
          message: `Your request to join ${bandData.bandFullName} has been accepted`,
          bandName: bandData.bandFullName,
          recipientIds: [requestData.userId],
          actionBy: {
            userId: respondedBy.userId,
            userName: respondedBy.userName,
            fullName: respondedBy.fullName,
            profilePictureUrl: respondedBy.profilePictureUrl
          }
        });
      } else {
        // Create REQUEST_REJECTED notification
        await createBandNotification({
          bandId,
          type: NOTIFICATION_TYPES.REQUEST_REJECTED,
          title: 'Join Request Rejected',
          message: `Your request to join ${bandData.bandFullName} has been declined`,
          bandName: bandData.bandFullName,
          recipientIds: [requestData.userId],
          actionBy: {
            userId: respondedBy.userId,
            userName: respondedBy.userName,
            fullName: respondedBy.fullName,
            profilePictureUrl: respondedBy.profilePictureUrl
          }
        });
      }
      
      return true;
    });
  } catch (error) {
    console.error('Error handling membership request:', error);
    throw error;
  }
};

/**
 * Clean up pending requests for deleted bands
 * @param {string} bandId - The ID of the band that was deleted
 * @returns {Promise<void>}
 */
export const cleanupPendingRequestsForDeletedBand = async (bandId) => {
  const db = getFirestoreInstance();
  
  try {
    // Find all users who have pending requests for this band
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('pendingActRequests', 'array-contains', { id: bandId, type: 'band' }));
    const querySnapshot = await getDocs(q);
    
    // For each user, remove the pending request
    const batchOp = db.batch();
    let batchCount = 0;
    const MAX_BATCH_SIZE = 500; // Firestore limit
    
    for (const userDoc of querySnapshot.docs) {
      const userData = userDoc.data();
      const updatedPendingRequests = userData.pendingActRequests.filter(
        req => !(req.type === 'band' && req.id === bandId)
      );
      
      batchOp.update(doc(db, 'users', userDoc.id), {
        pendingActRequests: updatedPendingRequests
      });
      
      batchCount++;
      
      // If batch is full, commit it and start a new one
      if (batchCount >= MAX_BATCH_SIZE) {
        await batchOp.commit();
        batchOp = db.batch();
        batchCount = 0;
      }
    }
    
    // Commit any remaining updates
    if (batchCount > 0) {
      await batchOp.commit();
    }
    
    console.log(`Cleaned up pending requests for deleted band ${bandId}`);
  } catch (error) {
    console.error('Error cleaning up pending requests:', error);
    throw error;
  }
};

/**
 * Update band details
 * @param {string} bandId - The ID of the band to update
 * @param {Object} updateData - The data to update
 * @param {Object} userData - The user performing the update
 * @returns {Promise<boolean>} - Success status
 */
export const updateBandDetails = async (bandId, updateData, userData) => {
  if (!bandId || !updateData || !userData) {
    throw new Error('Missing required parameters');
  }

  const db = getFirestoreInstance();
  const bandRef = doc(db, 'bands', bandId);
  
  try {
    const bandSnapshot = await getDoc(bandRef);
    if (!bandSnapshot.exists()) {
      throw new Error('Band not found');
    }

    const bandData = bandSnapshot.data();
    const allMemberIds = [...new Set(bandData.members.map(member => member.userId))];

    // Update the band data
    await runTransaction(db, async (transaction) => {
      // Get all user documents first
      const userDocs = await Promise.all(
        allMemberIds.map(async (userId) => {
          const userRef = doc(db, 'users', userId);
          return {
            ref: userRef,
            doc: await transaction.get(userRef)
          };
        })
      );

      // Update band document
      transaction.update(bandRef, updateData);

      // Update all user documents - both bands array and associatedActs
      for (const { ref, doc } of userDocs) {
        if (doc.exists()) {
          const userData = doc.data();
          
          // Update associatedActs
          const updatedAssociatedActs = userData.associatedActs?.map(act => {
            if (act.type === 'band' && act.id === bandId) {
              return {
                ...act,
                name: updateData.bandFullName || act.name,
                photoUrl: updateData.bandProfilePictureUrl || act.photoUrl,
                genres: updateData.bandGenres || act.genres,
              };
            }
            return act;
          }) || [];
          
          // Update bands array if it exists
          const updatedBands = userData.bands?.map(band => {
            if (band.id === bandId) {
              return {
                ...band,
                name: updateData.bandFullName || band.name,
                photoUrl: updateData.bandProfilePictureUrl || band.photoUrl,
                genres: updateData.bandGenres || band.genres,
              };
            }
            return band;
          }) || [];

          transaction.update(ref, {
            associatedActs: updatedAssociatedActs,
            bands: updatedBands
          });
        }
      }
    });

    // Create BAND_UPDATED notification
    await createBandNotification({
      bandId,
      type: NOTIFICATION_TYPES.BAND_UPDATED,
      title: 'Band Details Updated',
      message: `${userData.fullName} updated the band details for ${bandData.bandFullName}`,
      bandName: bandData.bandFullName,
      recipientIds: allMemberIds,
      actionBy: {
        userId: userData.userId,
        userName: userData.userName,
        fullName: userData.fullName,
        profilePictureUrl: userData.profilePictureUrl
      }
    });

    return true;
  } catch (error) {
    console.error('Error updating band:', error);
    throw error;
  }
};

/**
 * Remove a member from a band, updating both band and user documents
 * @param {string} bandId - The ID of the band
 * @param {string} memberUserId - The ID of the member to remove
 * @param {string} actorUserId - The ID of the user performing the action
 * @returns {Promise<boolean>} - Success status
 */
export const removeBandMember = async (bandId, memberUserId, actorUserId) => {
  if (!bandId || !memberUserId || !actorUserId) {
    throw new Error('Missing required parameters');
  }

  const db = getFirestoreInstance();
  
  try {
    return await runTransaction(db, async (transaction) => {
      // Get band and user documents
      const bandRef = doc(db, 'bands', bandId);
      const userRef = doc(db, 'users', memberUserId);
      
      const bandDoc = await transaction.get(bandRef);
      const userDoc = await transaction.get(userRef);
      
      if (!bandDoc.exists()) {
        throw new Error('Band not found');
      }
      
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }
      
      const bandData = bandDoc.data();
      const userData = userDoc.data();
      
      // Find the member in the band
      const member = bandData.members?.find(m => m.userId === memberUserId);
      
      if (!member) {
        throw new Error('Member not found in band');
      }
      
      const isAdmin = member.role === 'admin';
      const adminCount = bandData.members.filter(m => m.role === 'admin').length;
      const isLastAdmin = isAdmin && adminCount === 1;
      
      // Special handling for last admin
      if (isLastAdmin) {
        const regularMembers = bandData.members.filter(m => m.role === 'member');
        
        if (regularMembers.length === 0) {
          // If no regular members, handle band deletion separately
          throw new Error('Cannot remove last admin with no regular members to promote');
        } else {
          // Promote oldest member to admin
          const sortedMembers = [...regularMembers].sort((a, b) => {
            const aTime = a.joinedAt ? a.joinedAt.toMillis() : 0;
            const bTime = b.joinedAt ? b.joinedAt.toMillis() : 0;
            return aTime - bTime; // Oldest first
          });
          
          const newAdmin = sortedMembers[0];
          
          // Update members array:
          // 1. Remove departing admin
          // 2. Promote new admin
          const updatedMembers = bandData.members
            .filter(m => m.userId !== memberUserId)
            .map(m => m.userId === newAdmin.userId 
              ? { ...m, role: 'admin' } 
              : m
            );
          
          transaction.update(bandRef, {
            members: updatedMembers
          });
          
          // Update promoted user's bands array and associatedActs
          const promotedUserRef = doc(db, 'users', newAdmin.userId);
          const promotedUserDoc = await transaction.get(promotedUserRef);
          
          if (promotedUserDoc.exists()) {
            const promotedUserData = promotedUserDoc.data();
            
            // Update bands array
            if (promotedUserData.bands) {
              const updatedBands = promotedUserData.bands.map(band => {
                if (band.id === bandId) {
                  return { ...band, role: 'admin' };
                }
                return band;
              });
              
              transaction.update(promotedUserRef, {
                bands: updatedBands
              });
            }
            
            // Update associatedActs for display purposes
            if (promotedUserData.associatedActs) {
              const updatedAssociatedActs = promotedUserData.associatedActs.map(act => {
                if (act.type === 'band' && act.id === bandId) {
                  return { ...act, role: 'admin' };
                }
                return act;
              });
              
              transaction.update(promotedUserRef, {
                associatedActs: updatedAssociatedActs
              });
            }
          }
          
          // Create notification for promoting new admin
          await createBandNotification({
            bandId,
            type: NOTIFICATION_TYPES.ADMIN_ADDED,
            title: 'New Band Admin',
            message: `${newAdmin.fullName} has been automatically promoted to admin of ${bandData.bandFullName}`,
            bandName: bandData.bandFullName,
            recipientIds: bandData.members
              .filter(m => m.userId !== memberUserId)
              .map(m => m.userId),
            actionBy: {
              userId: actorUserId,
              userName: bandData.members.find(m => m.userId === actorUserId)?.userName,
              fullName: bandData.members.find(m => m.userId === actorUserId)?.fullName,
              profilePictureUrl: bandData.members.find(m => m.userId === actorUserId)?.profilePictureUrl
            }
          });
        }
      } else {
        // Normal member removal - update members array
        transaction.update(bandRef, {
          members: bandData.members.filter(m => m.userId !== memberUserId)
        });
      }
      
      // Remove band from user's bands array
      if (userData.bands) {
        const updatedBands = userData.bands.filter(
          band => band.id !== bandId
        );
        
        transaction.update(userRef, {
          bands: updatedBands
        });
      }
      
      // Remove band from user's associatedActs
      const updatedAssociatedActs = userData.associatedActs.filter(
        act => !(act.type === 'band' && act.id === bandId)
      );
      
      transaction.update(userRef, {
        associatedActs: updatedAssociatedActs
      });
      
      // Create notification for member leaving
      await createBandNotification({
        bandId,
        type: NOTIFICATION_TYPES.USER_REMOVED,
        title: 'Member Left',
        message: `${member.fullName} has left ${bandData.bandFullName}`,
        bandName: bandData.bandFullName,
        recipientIds: [
          ...bandData.members.filter(m => m.userId !== memberUserId).map(m => m.userId),
          memberUserId // Include removed member
        ],
        actionBy: {
          userId: actorUserId,
          userName: bandData.members.find(m => m.userId === actorUserId)?.userName,
          fullName: bandData.members.find(m => m.userId === actorUserId)?.fullName,
          profilePictureUrl: bandData.members.find(m => m.userId === actorUserId)?.profilePictureUrl
        }
      });
      
      return true;
    });
  } catch (error) {
    console.error('Error removing band member:', error);
    throw error;
  }
};

/**
 * Handle band deletion including cleaning up all related data
 * @param {string} bandId - The ID of the band to delete
 * @param {string} userId - The ID of the user performing the deletion
 * @returns {Promise<boolean>} - Success status
 */
export const deleteBand = async (bandId, userId) => {
  const db = getFirestoreInstance();
  
  try {
    // First get band data to know which users to update
    const bandRef = doc(db, 'bands', bandId);
    const bandDoc = await getDoc(bandRef);
    
    if (!bandDoc.exists()) {
      throw new Error('Band not found');
    }
    
    const bandData = bandDoc.data();
    
    // Get all member user IDs
    const memberUserIds = bandData.members.map(member => member.userId);
    
    // Start a transaction to update all affected users
    await runTransaction(db, async (transaction) => {
      // Get all user documents in the transaction
      const userDocs = await Promise.all(
        memberUserIds.map(async (userId) => {
          const userRef = doc(db, 'users', userId);
          return {
            ref: userRef,
            doc: await transaction.get(userRef)
          };
        })
      );
      
      // Update each user document to remove the band from both bands array and associatedActs
      for (const { ref, doc } of userDocs) {
        if (doc.exists()) {
          const userData = doc.data();
          
          // Remove from associatedActs
          const updatedAssociatedActs = userData.associatedActs?.filter(
            act => !(act.type === 'band' && act.id === bandId)
          ) || [];
          
          // Remove from bands array
          const updatedBands = userData.bands?.filter(
            band => band.id !== bandId
          ) || [];
          
          transaction.update(ref, {
            associatedActs: updatedAssociatedActs,
            bands: updatedBands
          });
        }
      }
      
      // Delete the band document
      transaction.delete(bandRef);
    });
    
    // After successful transaction, clean up pending requests
    // (This is done outside the transaction because it might involve many users)
    await cleanupPendingRequestsForDeletedBand(bandId);
    
    // Create notification for band deletion
    await createBandNotification({
      bandId,
      type: NOTIFICATION_TYPES.BAND_DELETED,
      title: 'Band Deleted',
      message: `The band ${bandData.bandFullName} has been deleted`,
      bandName: bandData.bandFullName,
      recipientIds: memberUserIds,
      actionBy: {
        userId,
        userName: bandData.members.find(m => m.userId === userId)?.userName,
        fullName: bandData.members.find(m => m.userId === userId)?.fullName,
        profilePictureUrl: bandData.members.find(m => m.userId === userId)?.profilePictureUrl
      }
    });
    
    return true;
  } catch (error) {
    console.error('Error deleting band:', error);
    throw error;
  }
};

/**
 * Update member role to admin
 * @param {string} bandId - The ID of the band
 * @param {string} memberId - The ID of the member to promote
 * @param {string} actorId - The ID of the user performing the action
 * @returns {Promise<boolean>} - Success status
 */
export const addBandAdmin = async (bandId, memberId, actorId) => {
  const db = getFirestoreInstance();
  try {
    return await runTransaction(db, async (transaction) => {
      // Get band and user documents
      const bandRef = doc(db, 'bands', bandId);
      const userRef = doc(db, 'users', memberId);
      
      const bandDoc = await transaction.get(bandRef);
      const userDoc = await transaction.get(userRef);
      
      if (!bandDoc.exists()) {
        throw new Error('Band not found');
      }
      
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }
      
      const bandData = bandDoc.data();
      const userData = userDoc.data();
      
      // Find the member
      const member = bandData.members.find(m => m.userId === memberId && m.role === 'member');
      
      if (!member) {
        throw new Error('Member not found or already an admin');
      }
      
      // Update the member's role in the band document
      const updatedMembers = bandData.members.map(m => 
        m.userId === memberId ? { ...m, role: 'admin' } : m
      );
      
      transaction.update(bandRef, {
        members: updatedMembers
      });
      
      // Update the user's bands array
      if (userData.bands) {
        const updatedBands = userData.bands.map(band => {
          if (band.id === bandId) {
            return { ...band, role: 'admin' };
          }
          return band;
        });
        
        transaction.update(userRef, {
          bands: updatedBands
        });
      }
      
      // Update the user's associatedActs
      const updatedAssociatedActs = userData.associatedActs.map(act => {
        if (act.type === 'band' && act.id === bandId) {
          return { ...act, role: 'admin' };
        }
        return act;
      });
      
      transaction.update(userRef, {
        associatedActs: updatedAssociatedActs
      });
      
      // Create notification
      const actorMember = bandData.members.find(m => m.userId === actorId);
      
      await createBandNotification({
        bandId,
        type: NOTIFICATION_TYPES.ADMIN_ADDED,
        title: 'New Band Admin',
        message: `${member.fullName} has been made an admin of ${bandData.bandFullName}`,
        bandName: bandData.bandFullName,
        recipientIds: bandData.members.map(m => m.userId),
        actionBy: {
          userId: actorId,
          userName: actorMember?.userName,
          fullName: actorMember?.fullName,
          profilePictureUrl: actorMember?.profilePictureUrl
        }
      });
      
      return true;
    });
  } catch (error) {
    console.error('Error adding band admin:', error);
    throw error;
  }
};

/**
 * Demote an admin to regular member
 * @param {string} bandId - The ID of the band
 * @param {string} adminId - The ID of the admin to demote
 * @param {string} actorId - The ID of the user performing the action
 * @returns {Promise<boolean>} - Success status
 */
export const removeBandAdmin = async (bandId, adminId, actorId) => {
  const db = getFirestoreInstance();
  try {
    return await runTransaction(db, async (transaction) => {
      // Get band and user documents
      const bandRef = doc(db, 'bands', bandId);
      const userRef = doc(db, 'users', adminId);
      
      const bandDoc = await transaction.get(bandRef);
      const userDoc = await transaction.get(userRef);
      
      if (!bandDoc.exists()) {
        throw new Error('Band not found');
      }
      
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }
      
      const bandData = bandDoc.data();
      const userData = userDoc.data();
      
      // Find the admin
      const admin = bandData.members.find(m => m.userId === adminId && m.role === 'admin');
      
      if (!admin) {
        throw new Error('Admin not found');
      }
      
      // Check if this is the last admin
      const adminCount = bandData.members.filter(m => m.role === 'admin').length;
      if (adminCount <= 1) {
        throw new Error('Cannot remove the last admin');
      }
      
      // Update the admin's role in the band document
      const updatedMembers = bandData.members.map(m => 
        m.userId === adminId ? { ...m, role: 'member' } : m
      );
      
      transaction.update(bandRef, {
        members: updatedMembers
      });
      
      // Update the user's bands array
      if (userData.bands) {
        const updatedBands = userData.bands.map(band => {
          if (band.id === bandId) {
            return { ...band, role: 'member' };
          }
          return band;
        });
        
        transaction.update(userRef, {
          bands: updatedBands
        });
      }
      
      // Update the user's associatedActs
      const updatedAssociatedActs = userData.associatedActs.map(act => {
        if (act.type === 'band' && act.id === bandId) {
          return { ...act, role: 'member' };
        }
        return act;
      });
      
      transaction.update(userRef, {
        associatedActs: updatedAssociatedActs
      });
      
      // Create notification
      const actorMember = bandData.members.find(m => m.userId === actorId);
      
      await createBandNotification({
        bandId,
        type: NOTIFICATION_TYPES.ADMIN_REMOVED,
        title: 'Band Admin Removed',
        message: `${admin.fullName} is no longer an admin of ${bandData.bandFullName}`,
        bandName: bandData.bandFullName,
        recipientIds: bandData.members.map(m => m.userId),
        actionBy: {
          userId: actorId,
          userName: actorMember?.userName,
          fullName: actorMember?.fullName,
          profilePictureUrl: actorMember?.profilePictureUrl
        }
      });
      
      return true;
    });
  } catch (error) {
    console.error('Error removing band admin:', error);
    throw error;
  }
};

/**
 * Find all bands where a user has requests, memberships, or admin roles
 * Useful for user profile pages or account management
 * @param {string} userId - The user ID to find bands for
 * @returns {Promise<{bands: Array, pendingRequests: Array}>} - Object containing bands and pending requests
 */
export const findBandsForUser = async (userId) => {
  const db = getFirestoreInstance();
  
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }
    
    const userData = userDoc.data();
    const result = {
      bands: [],
      pendingRequests: []
    };
    
    // Get bands from bands array
    if (userData.bands && userData.bands.length > 0) {
      const bandIds = userData.bands.map(band => band.id);
      
      if (bandIds.length > 0) {
        // Fetch band details in batches (Firestore limitation)
        const batchSize = 30;
        const batches = [];
        
        for (let i = 0; i < bandIds.length; i += batchSize) {
          batches.push(bandIds.slice(i, i + batchSize));
        }
        
        for (const batchIds of batches) {
          const batchPromises = batchIds.map(id => getDoc(doc(db, 'bands', id)));
          const batchResults = await Promise.all(batchPromises);
          
          batchResults.forEach(bandDoc => {
            if (bandDoc.exists()) {
              const bandData = bandDoc.data();
              const bandRecord = userData.bands.find(
                band => band.id === bandDoc.id
              );
              
              result.bands.push({
                id: bandDoc.id,
                ...bandData,
                userRole: bandRecord?.role || 'member'
              });
            }
          });
        }
      }
    }
    
    // Get bands from pendingActRequests
    if (userData.pendingActRequests && userData.pendingActRequests.length > 0) {
      const pendingBandIds = userData.pendingActRequests
        .filter(req => req.type === 'band')
        .map(req => req.id);
      
      if (pendingBandIds.length > 0) {
        // Fetch band details in batches
        const batchSize = 30;
        const batches = [];
        
        for (let i = 0; i < pendingBandIds.length; i += batchSize) {
          batches.push(pendingBandIds.slice(i, i + batchSize));
        }
        
        for (const batchIds of batches) {
          const batchPromises = batchIds.map(id => getDoc(doc(db, 'bands', id)));
          const batchResults = await Promise.all(batchPromises);
          
          batchResults.forEach(bandDoc => {
            if (bandDoc.exists()) {
              const bandData = bandDoc.data();
              
              // Double-check that the request still exists in the band
              const requestStillExists = bandData.memberRequests?.some(
                req => req.userId === userId
              );
              
              if (requestStillExists) {
                result.pendingRequests.push({
                  id: bandDoc.id,
                  bandFullName: bandData.bandFullName,
                  bandUserName: bandData.bandUserName,
                  bandProfilePictureUrl: bandData.bandProfilePictureUrl,
                  bandGenres: bandData.bandGenres,
                  requestDate: bandData.memberRequests.find(req => req.userId === userId)?.timestamp
                });
              }
            }
          });
        }
      }
    }
    
    return result;
  } catch (error) {
    console.error('Error finding bands for user:', error);
    throw error;
  }
};

/**
 * Cancel a pending band membership request
 * @param {string} bandId - The ID of the band
 * @param {string} userId - The ID of the user canceling the request
 * @returns {Promise<boolean>} - Success status
 */
export const cancelBandMembershipRequest = async (bandId, userId) => {
  const db = getFirestoreInstance();
  
  try {
    const bandRef = doc(db, 'bands', bandId);
    const userRef = doc(db, 'users', userId);
    
    return await runTransaction(db, async (transaction) => {
      // Get latest documents
      const bandDoc = await transaction.get(bandRef);
      const userDoc = await transaction.get(userRef);
      
      if (!bandDoc.exists()) {
        throw new Error('Band not found');
      }
      
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }
      
      const bandData = bandDoc.data();
      const userData = userDoc.data();
      
      // Find and remove request from band
      const updatedRequests = (bandData.memberRequests || []).filter(
        req => req.userId !== userId
      );
      
      // Find and remove request from user
      const updatedPendingRequests = (userData.pendingActRequests || []).filter(
        req => !(req.type === 'band' && req.id === bandId)
      );
      
      // Update both documents
      transaction.update(bandRef, {
        memberRequests: updatedRequests
      });
      
      transaction.update(userRef, {
        pendingActRequests: updatedPendingRequests
      });
      
      return true;
    });
  } catch (error) {
    console.error('Error canceling band membership request:', error);
    throw error;
  }
};

/**
 * Check for stale pending requests and clean them up
 * Useful to run periodically (e.g., daily) to keep data consistent
 * @param {number} maxAgeDays - Maximum age in days for a pending request
 * @returns {Promise<{removed: number}>} - Number of removed requests
 */
export const cleanupStalePendingRequests = async (maxAgeDays = 30) => {
  const db = getFirestoreInstance();
  
  try {
    const now = new Date();
    const cutoffDate = new Date(now.setDate(now.getDate() - maxAgeDays));
    const cutoffTimestamp = Timestamp.fromDate(cutoffDate);
    
    // Query all bands with member requests
    const bandsRef = collection(db, 'bands');
    const bandsSnapshot = await getDocs(bandsRef);
    
    let removedCount = 0;
    const MAX_BATCH_SIZE = 500;
    let batchOp = db.batch();
    let batchCount = 0;
    
    // Track users who need updates
    const userUpdates = new Map();
    
    // Process each band
    for (const bandDoc of bandsSnapshot.docs) {
      const bandId = bandDoc.id;
      const bandData = bandDoc.data();
      
      if (!bandData.memberRequests || bandData.memberRequests.length === 0) {
        continue;
      }
      
      // Find stale requests
      const staleRequests = bandData.memberRequests.filter(req => 
        req.timestamp && req.timestamp.toDate() < cutoffDate
      );
      
      if (staleRequests.length > 0) {
        // Update band document to remove stale requests
        const updatedRequests = bandData.memberRequests.filter(req => 
          !staleRequests.some(stale => stale.userId === req.userId)
        );
        
        batchOp.update(doc(db, 'bands', bandId), {
          memberRequests: updatedRequests
        });
        
        batchCount++;
        removedCount += staleRequests.length;
        
        // Track users who need updates
        staleRequests.forEach(req => {
          if (!userUpdates.has(req.userId)) {
            userUpdates.set(req.userId, []);
          }
          userUpdates.get(req.userId).push(bandId);
        });
        
        // Commit batch if it's full
        if (batchCount >= MAX_BATCH_SIZE) {
          await batchOp.commit();
          batchOp = db.batch();
          batchCount = 0;
        }
      }
    }
    
    // Commit remaining band updates
    if (batchCount > 0) {
      await batchOp.commit();
      batchOp = db.batch();
      batchCount = 0;
    }
    
    // Update user documents
    for (const [userId, bandIds] of userUpdates.entries()) {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        if (userData.pendingActRequests && userData.pendingActRequests.length > 0) {
          const updatedPendingRequests = userData.pendingActRequests.filter(
            req => !(req.type === 'band' && bandIds.includes(req.id))
          );
          
          batchOp.update(userRef, {
            pendingActRequests: updatedPendingRequests
          });
          
          batchCount++;
          
          // Commit batch if it's full
          if (batchCount >= MAX_BATCH_SIZE) {
            await batchOp.commit();
            batchOp = db.batch();
            batchCount = 0;
          }
        }
      }
    }
    
    // Commit remaining user updates
    if (batchCount > 0) {
      await batchOp.commit();
    }
    
    return { removed: removedCount };
  } catch (error) {
    console.error('Error cleaning up stale pending requests:', error);
    throw error;
  }
};

// Export all functions
export default {
  isUserBandAdmin,
  isUserBandMember,
  getAdminMembers,
  getRegularMembers,
  sendBandMembershipRequest,
  handleMembershipRequest,
  cleanupPendingRequestsForDeletedBand,
  updateBandDetails,
  removeBandMember,
  deleteBand,
  addBandAdmin,
  removeBandAdmin,
  findBandsForUser,
  cancelBandMembershipRequest,
  cleanupStalePendingRequests
};